import type { NextApiRequest, NextApiResponse } from 'next'
import type { IncomingMessage, ServerResponse } from 'http'
import logger from '@reward-platform/logger/edge'
import { createServerLoggerMeta } from '@reward-platform/logger/utils'
import {
  ErrorResponseSchema,
  type ErrorResponse,
} from '@reward-platform/shared-api/exceptions/ErrorResponse'
import { Auth0B2CException } from './Auth0B2CException'

/**
 * Sanitizes an error response by extracting only safe fields.
 * This breaks taint tracking by creating a new plain object with no reference to the error.
 */
const sanitizeErrorResponse = (errorResponse: ErrorResponse): ErrorResponse => {
  const {
    code,
    businessMessage,
    developerMessage,
    unrecoverable,
    kind,
    subtype,
    validationErrors,
  } = ErrorResponseSchema.parse(errorResponse)
  return {
    code,
    businessMessage,
    developerMessage,
    unrecoverable,
    ...(kind && { kind }),
    ...(subtype && { subtype }),
    ...(validationErrors && { validationErrors }),
  }
}

export const logAuth0Error = (
  message: string,
  err: Auth0B2CException,
  req: IncomingMessage,
  res: ServerResponse
) => {
  // Log error to datadog
  const loggerContext = createServerLoggerMeta(req, res)
  logger.error(message, {
    ...err.formatErrorForLogs('auth_b2c'),
    ...loggerContext,
  })
}

export const handleAuth0ErrorStatus = (
  err: Auth0B2CException,
  req: NextApiRequest,
  res: NextApiResponse
) => {
  // Validate and sanitize error response to prevent information leakage
  // Extract safe data into plain variables to break taint tracking
  const { httpStatus } = err
  const errorResponse = err.getErrorResponse()
  const safeResponse = sanitizeErrorResponse(errorResponse)
  res.status(httpStatus).send(safeResponse)
}
